import { computed, ref } from 'vue';
import { Contents, EpubCFI } from 'epubjs';
import Manager from 'epubjs/types/managers/manager';
import type Section from 'epubjs/types/section';
import { Manifest } from '../util/manifest';
import { EpubJsSpineItem, EpubJsBook, EpubJsSpine } from '../types/epubjs';
import { Track } from '../types';
import View from 'epubjs/types/managers/view';
import useNavigationStore from '../stores/navigation';
import { fixCfiSpineId } from '../util/location';

const manifest = ref<Manifest>();
let book: Partial<EpubJsBook>;
let isLocalEpubFile: boolean;
// Cache to track which spine items have already been loaded
const loadedSpineItems = new Set<string>();
// Cache for in-progress loading operations to prevent duplicate simultaneous requests
const loadingSpineItems = new Map<string, Promise<void>>();

const useReaderEngine = () => {
  const navigationStore = useNavigationStore();

  const initialise = async (m: Manifest, b: EpubJsBook, _isLocalEpubFile: boolean) => {
    manifest.value = m;
    book = b;
    isLocalEpubFile = _isLocalEpubFile;

    // Clear the cache when initializing a new book
    loadedSpineItems.clear();
    loadingSpineItems.clear();

    navigationStore.$reset();
    await navigationStore.initializeNavigation(m, b, getElementCFI);
    // Non-blocking — must NOT be awaited, must NOT delay first render
    navigationStore.initializeLoi(b, m.spineItems).catch(console.error);
  };

  const getCurrentPages = (): EpubJsSpineItem[] => {
    const viewManager = (book.rendition as any).manager as Manager;

    return (
      viewManager
        .visible()

        // @ts-ignore
        .map((view) => view.section.idref)
        .map((id) => manifest.value!.spineItems[id])
    );
  };

  const getIsLocalEpub = (): boolean => isLocalEpubFile;

  const getSpineItem = (identifier: string): EpubJsSpineItem | null => {
    if (!book.spine) return null;
    const spine = book.spine as EpubJsSpine;
    if (!spine.spineItems) return null;
    return spine.spineItems.find((item) => item.path && item.path.endsWith(identifier)) || null;
  };
  /**
   * Non-throwing lookup of a spine item by index, id, href or epubCfi.
   *
   * Returns null whenever the identifier cannot be resolved. That is a legitimate outcome, not
   * an exceptional one: a shared deeplink can carry a malformed CFI (link auto-detection in
   * chat/mail clients strips the trailing `])` of an unencoded CFI), and audio auto-advance can
   * hand us an href that is not part of this book's spine at all.
   *
   * @param identifier string | number
   */
  const tryGetSpineByIdentifier = (identifier: string | number): Section | null => {
    if (!book.spine) {
      return null;
    }
    const spineItem = book.spine.get(identifier);
    if (spineItem) {
      return spineItem;
    }
    const spineItemByHref = getSpineItem(identifier as string);
    if (!spineItemByHref) {
      return null;
    }
    return book.spine.get(spineItemByHref.href) || null;
  };

  /**
   * Get spine by index, id, href, epubCfi. Throws when the identifier cannot be resolved.
   * Prefer {@link tryGetSpineByIdentifier} at call sites where a miss is recoverable.
   * @param identifier string | number
   */
  const getSpineByIdentifier = (identifier: string | number): Section => {
    if (!book.spine) {
      throw new Error('Book spine is not initialized');
    }
    const spineItem = tryGetSpineByIdentifier(identifier);
    if (!spineItem) {
      throw new Error('Failed to load spine items');
    }
    return spineItem;
  };

  const createCfiFromSpineItem = (spineItem: Section, elementId?: string) => {
    // console.debug('Creating CFI from spine item:', spineItem);
    if (!spineItem) {
      throw new Error('Spine item is required to create an EpubCFI.');
    }

    // Generate the base CFI for the start of the spine item
    const spineIndex = spineItem.index; // Index of the spine item in the spine
    const spineId = spineItem.idref; // Spine ID of idref

    if (spineIndex == null || !spineId) {
      throw new Error('Invalid spine item: Missing index or id.');
    }

    try {
      /**
       * The CFI string for the start of the spine item.
       * - /6/ - Navigate to the spine section of the EPUB package.
       * - /<spineIndex * 2 + 1>[<spineId>] - Navigate to the specific spine item.
       * - ! - Terminates the spine path and marks the end of the container reference.
       *       Everything after this points to a location within the spine item.
       *
       * Epubjs display failed as it expected an element. A lot of PG books don't have an element from ToC. So set a dummy element.
       * - /1/1:0 - /<first element>/<first child>:<cursor> The path to the start of the spine item
       */
      const cfiString = `epubcfi(/6/${spineIndex * 2 + 1}[${spineId}]!/1/1${elementId ? `[${elementId}]` : `:0`})`;
      // console.debug('CFI:', cfiString);

      const epubCFI = new EpubCFI();
      if (!epubCFI.isCfiString(cfiString)) {
        throw new Error('Generated CFI string is not valid');
      }

      return cfiString;
    } catch (error) {
      console.error('Error creating CFI from spine item:', error);
      // Return a simpler CFI that points just to the spine item without DOM references
      const fallbackCfiString = `epubcfi(/6/${spineIndex * 2 + 1}[${spineId}]!)`;
      console.debug('Fallback CFI:', fallbackCfiString);
      return fallbackCfiString;
    }
  };

  const NEEDS_TEXT_OFFSET_REGEX = /(\[[^\]]+\]|\/\d+)$/;

  const getElementCFI = async (spineItemIdentifier: string, elementId: string): Promise<string> => {
    try {
      // Safer path sanitization (removes leading ../ just in case)
      const identifier = spineItemIdentifier.replace(/^\.\.\//, '');

      const spineItem = tryGetSpineByIdentifier(identifier);
      if (!spineItem) {
        console.warn('No spine item found for identifier:', identifier);
        return 'epubcfi(/6/2!)';
      }

      // Ensure the document is loaded (check cache first)
      const spineItemKey = spineItem.href || identifier;
      if (!spineItem.document && !loadedSpineItems.has(spineItemKey)) {
        // If already loading, wait for that operation to complete
        if (loadingSpineItems.has(spineItemKey)) {
          try {
            await loadingSpineItems.get(spineItemKey);
          } catch (loadError) {
            console.error('Failed to load spine item contents:', loadError);
            return createCfiFromSpineItem(spineItem);
          }
        } else {
          // Start a new loading operation and cache the promise
          const loadPromise = (async () => {
            try {
              await spineItem.load(book.load.bind(book));
              loadedSpineItems.add(spineItemKey);
            } finally {
              // Clean up the loading promise once complete
              loadingSpineItems.delete(spineItemKey);
            }
          })();

          loadingSpineItems.set(spineItemKey, loadPromise);

          try {
            await loadPromise;
          } catch (loadError) {
            console.error('Failed to load spine item contents:', loadError);
            return createCfiFromSpineItem(spineItem);
          }
        }
      }

      // If no specific element ID, just point to the start of the chapter/spine item
      if (!elementId || elementId.trim() === '') {
        return createCfiFromSpineItem(spineItem);
      }

      // Attempt to find the element in the DOM
      let element = null;
      try {
        element = spineItem.document?.getElementById(elementId);
      } catch (domError) {
        console.warn('Error accessing DOM in spine item:', domError);
      }

      if (!element) {
        /** * FALLBACK: Element not found in DOM.
         * Standard CFI ID assertion syntax: /spine_path!/root_node[elementId]
         * We use /1/1 to point to the very beginning of the body as a safe base.
         */
        console.debug(`Element ${elementId} not found in DOM, using standard ID assertion.`);
        return createCfiFromSpineItem(spineItem, elementId);
      }

      try {
        const cfi = spineItem.cfiFromElement(element);
        if (!cfi) throw new Error('CFI generation returned empty');

        let fixedCfi = cfi;

        fixedCfi = fixCfiSpineId(fixedCfi, spineItem.idref);

        if (fixedCfi.includes('!') && !fixedCfi.includes('!/')) {
          fixedCfi = fixedCfi.replace('!', '!/');
        }

        if (NEEDS_TEXT_OFFSET_REGEX.test(fixedCfi)) {
          fixedCfi = `${fixedCfi}/1:0`;
        }

        return fixedCfi;
      } catch (err) {
        /**
         * FALLBACK: Internal Epub.js cfiFromElement failed.
         * Construct the CFI manually using the spine coordinates and the element ID.
         */
        console.warn('cfiFromElement failed, building manual CFI for:', elementId);
        return createCfiFromSpineItem(spineItem, elementId);
      }
    } catch (globalError) {
      console.error('Critical failure in getElementCFI:', globalError);
      return 'epubcfi(/6/2!)';
    }
  };

  /**
   * Make sure that all visible spine items were loaded at that point.
   * If not sure - use async version.
   */
  const getElementCFISync = (spineItemIdentifier: string, elementId: string): string => {
    let identifier = spineItemIdentifier;
    if (spineItemIdentifier.includes('..')) {
      // For full text epubs
      identifier = spineItemIdentifier.replace('../', '');
    }
    const spineItem = getSpineByIdentifier(identifier);
    const element = spineItem.document.getElementById(elementId);

    if (!element) {
      try {
        return createCfiFromSpineItem(spineItem);
      } catch (error) {
        console.error('Failed to create CFI from spine item.', error);
        return getCurrentEpubCFI();
      }
    }
    return spineItem.cfiFromElement(element);
  };

  const getCFIFromSpineIdentifier = async (target: string): Promise<string> => {
    if (book.rendition.epubcfi.isCfiString(target)) {
      return target;
    }
    const [href, id] = target.split('#');
    return getElementCFI(href, id);
  };

  /**
   * Get pageSpineItem by index, id, href, epubCfi.
   * @param spineIdentifier string | number
   */
  const getPageSpineItem = (spineIdentifier: string | number): EpubJsSpineItem => {
    const { idref } = getSpineByIdentifier(spineIdentifier);
    return manifest.value!.spineItems[idref];
  };

  const getViewByTrack = (track: Track): View | undefined => {
    const { srcFile } = track.text;
    const spine = getSpineByIdentifier(srcFile);
    return (book.rendition as any)?.manager?.views?.find(spine);
  };

  const getIframeByTrack = (track: Track): Document => {
    const view = getViewByTrack(track);
    if (!view) {
      throw Error('Failed to find Iframe for the track.');
    }

    // @ts-ignore - we should not really be using private properties, but ... :shrug:
    return view.document;
  };

  const getCurrentPagesContents = (): Contents[] => {
    const viewManager = (book?.rendition as any)?.manager as Manager;

    return viewManager?.visible?.().map((view) => (view as any).contents) ?? [];
  };

  const getBookResourceUrl = (resourcePath: string): string =>
    // @ts-ignore
    `${book.url.origin}${book.path.directory}${resourcePath}`;

  const getCurrentEpubCFI = (): string => {
    const { rendition } = book;

    // @ts-ignore
    const currentLocationStart = rendition.currentLocation().start;

    if (typeof currentLocationStart === 'undefined') {
      // Might need to me fixed in a future in a different way.
      // To reproduce the bug it's necessary to open converted daisy book, play some sentence, scroll down for couple
      // pages and resize browser. Then play some other chapters, resize browser back and scroll up.
      return ''; // very, very much an edge-case that we ignore
    }

    return currentLocationStart.cfi;
  };

  /**
   * Gets the next or previous spine item by giving the href and direction.
   * @param href - The href path of the spine item
   * @param direction - Navigation direction: 'next' or 'prev'
   * @returns The neighbouring spine item, or null if no manifest is loaded, spine is not found at boundary.
   */
  const getNeighbouringSpineItem = (href: string, direction: 'next' | 'prev'): EpubJsSpineItem | null => {
    if (!manifest.value) {
      return null;
    }

    const spineItems = Object.values(manifest.value.spineItems);
    const currentSpineItemIdx = spineItems.findIndex((spineItem) => spineItem.path === href);

    if (currentSpineItemIdx === -1) {
      console.debug(`Cannot find next spine item: href "${href}" not found in book spine.`);
      return null;
    }

    // Boundary checks, end of book or start of book.
    if (direction === 'next' && currentSpineItemIdx >= spineItems.length - 1) return null;
    if (direction === 'prev' && currentSpineItemIdx <= 0) return null;

    const targetIndex = direction === 'next' ? currentSpineItemIdx + 1 : currentSpineItemIdx - 1;
    return spineItems[targetIndex];
  };

  return {
    manifest: computed(() => {
      if (manifest.value === undefined) {
        throw new Error('Unexpected use of manifest before initialised.');
      }
      return manifest.value;
    }),
    initialise,
    getCurrentPages,
    getBookResourceUrl,
    getCurrentPagesContents,
    getPageSpineItem,
    getIframeByTrack,
    getViewByTrack,
    book: computed(() => book),
    getSpineByIdentifier,
    tryGetSpineByIdentifier,
    getIsLocalEpub,
    getCurrentEpubCFI,
    getElementCFI,
    getCFIFromSpineIdentifier,
    getElementCFISync,
    getNeighbouringSpineItem,
  };
};

export default useReaderEngine;
